import React, { useState, useRef, useEffect } from 'react';
import {
  Award,
  ShieldCheck,
  ArrowRight,
  AlertCircle,
  Sparkles,
  CheckCircle2,
  Clock,
  BookOpen,
  Zap,
  Brain,
  Layers,
  Compass,
  X,
  Menu,
  Check,
  BarChart3,
  Scale
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';

export const LoginView: React.FC = () => {
  const { loginWithGoogleCredential, loginDemo, error, clearError } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isGsiRendered, setIsGsiRendered] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isOpenBookModalOpen, setIsOpenBookModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedTrackTab, setSelectedTrackTab] = useState<'cpgp' | 'rac'>('cpgp');

  const heroGoogleBtnRef = useRef<HTMLDivElement>(null);
  const modalGoogleBtnRef = useRef<HTMLDivElement>(null);

  const clientId = '364307068339-pih48j7bdkp8gmo66at8nd5nf68dttlc.apps.googleusercontent.com';

  // Initialize Google Identity Services (GIS) on mount
  useEffect(() => {
    const initGsi = () => {
      const google = (window as any).google;
      if (google?.accounts?.id) {
        try {
          google.accounts.id.initialize({
            client_id: clientId,
            callback: async (res: any) => {
              if (res?.credential) {
                try {
                  setIsAuthenticating(true);
                  setAuthError(null);
                  clearError();
                  await loginWithGoogleCredential(res.credential);
                } catch (err: any) {
                  setAuthError(err.message || 'Google sign-in verification failed');
                  setIsAuthenticating(false);
                }
              }
            }
          });

          if (heroGoogleBtnRef.current) {
            google.accounts.id.renderButton(heroGoogleBtnRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              width: 300,
              text: 'continue_with',
              shape: 'pill'
            });
            setIsGsiRendered(true);
          }

          if (modalGoogleBtnRef.current) {
            google.accounts.id.renderButton(modalGoogleBtnRef.current, {
              type: 'standard',
              theme: 'outline',
              size: 'large',
              width: 300,
              text: 'signin_with',
              shape: 'pill'
            });
          }
        } catch {
          // ignore GSI render issues in restrictive environments
        }
      }
    };

    if ((window as any).google?.accounts?.id) {
      initGsi();
    } else {
      const interval = setInterval(() => {
        if ((window as any).google?.accounts?.id) {
          initGsi();
          clearInterval(interval);
        }
      }, 250);
      return () => clearInterval(interval);
    }
  }, [loginWithGoogleCredential, clearError]);

  // Re-render modal button if modal opens
  useEffect(() => {
    if (isLoginModalOpen && (window as any).google?.accounts?.id && modalGoogleBtnRef.current) {
      try {
        (window as any).google.accounts.id.renderButton(modalGoogleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          width: 300,
          text: 'signin_with',
          shape: 'pill'
        });
        setIsGsiRendered(true);
      } catch {
        // ignore
      }
    }
  }, [isLoginModalOpen]);

  const handleGoogleSignIn = async () => {
    try {
      setIsAuthenticating(true);
      setAuthError(null);
      clearError();

      const google = (window as any).google;
      if (google?.accounts?.id) {
        google.accounts.id.prompt((notification: any) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            redirectToGoogleOAuth();
          }
        });
        return;
      }
      await redirectToGoogleOAuth();
    } catch (err: any) {
      setAuthError(err.message || 'Failed to initiate Google Sign-In');
      setIsAuthenticating(false);
    }
  };

  const redirectToGoogleOAuth = async () => {
    const authUrlData = await api.getGoogleAuthUrl();
    if (authUrlData?.url) {
      window.location.href = authUrlData.url;
    }
  };

  const handleDemoSignIn = async () => {
    try {
      setIsAuthenticating(true);
      setAuthError(null);
      clearError();
      await loginDemo();
    } catch (err: any) {
      setAuthError(err.message || 'Demo sign-in failed');
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans selection:bg-indigo-500/30 selection:text-indigo-900 dark:selection:text-indigo-200 transition-colors duration-200">
      {/* Sticky Marketing Navigation Bar */}
      <header className="border-b border-slate-200 dark:border-slate-800/80 bg-white/90 dark:bg-slate-950/80 backdrop-blur sticky top-0 z-50 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center space-x-3 shrink-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-500 via-indigo-600 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
              <Award className="w-5 h-5 text-white" />
            </div>
            <div className="shrink-0">
              <div className="flex items-center space-x-2">
                <span className="font-extrabold text-base sm:text-lg tracking-tight whitespace-nowrap bg-gradient-to-r from-indigo-600 via-cyan-600 to-slate-900 dark:from-indigo-300 dark:via-cyan-300 dark:to-slate-100 bg-clip-text text-transparent">
                  BioPharm Exam Prep
                </span>
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400 font-mono whitespace-nowrap hidden xl:block">
                ASQ CPGP & RAPS RAC Exam Simulator
              </span>
            </div>
          </div>

          {/* Desktop Navigation Links */}
          <nav className="hidden xl:flex items-center space-x-6 text-xs font-semibold text-slate-600 dark:text-slate-300 shrink-0">
            <a href="#tracks" className="whitespace-nowrap hover:text-indigo-600 dark:hover:text-indigo-400 transition">Certifications</a>
            <a href="#features" className="whitespace-nowrap hover:text-indigo-600 dark:hover:text-indigo-400 transition">Prometric Engine</a>
            <a href="#comparison" className="whitespace-nowrap hover:text-indigo-600 dark:hover:text-indigo-400 transition">Why BioPharm</a>
            <a href="#pricing" className="whitespace-nowrap hover:text-indigo-600 dark:hover:text-indigo-400 transition">Pricing</a>
            <button
              onClick={() => setIsOpenBookModalOpen(true)}
              className="whitespace-nowrap text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 cursor-pointer font-bold shrink-0"
            >
              <Compass className="w-3.5 h-3.5 shrink-0" />
              <span>Exam Day Guide</span>
            </button>
          </nav>

          {/* Right Action Controls */}
          <div className="flex items-center space-x-2 sm:space-x-3 shrink-0">
            <button
              onClick={() => setIsLoginModalOpen(true)}
              className="whitespace-nowrap px-3 py-1.5 rounded-xl border border-slate-300 dark:border-slate-700 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer shrink-0"
            >
              Sign In
            </button>

            <button
              onClick={() => setIsLoginModalOpen(true)}
              className="whitespace-nowrap px-3 sm:px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition shadow-md shadow-indigo-600/20 dark:shadow-indigo-950 flex items-center space-x-1.5 cursor-pointer shrink-0"
            >
              <span><span className="hidden sm:inline">14-Day </span>Free Trial</span>
              <ArrowRight className="w-3.5 h-3.5 shrink-0" />
            </button>

            {/* Mobile / Tablet Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="xl:hidden p-1.5 rounded-xl border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer shrink-0"
              aria-label="Toggle Navigation Menu"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile / Tablet Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="xl:hidden border-t border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-950/95 backdrop-blur px-4 py-3 space-y-2 text-xs font-semibold">
            <a
              href="#tracks"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 transition"
            >
              Certifications (ASQ CPGP & RAPS RAC)
            </a>
            <a
              href="#features"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 transition"
            >
              Prometric CBT Engine
            </a>
            <a
              href="#comparison"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 transition"
            >
              Why BioPharm vs ASQ Banks
            </a>
            <a
              href="#pricing"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-3 py-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-900 transition"
            >
              Pricing & 14-Day Free Trial
            </a>
            <button
              onClick={() => {
                setIsMobileMenuOpen(false);
                setIsOpenBookModalOpen(true);
              }}
              className="w-full text-left px-3 py-2 rounded-lg text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/50 transition font-bold flex items-center gap-2"
            >
              <Compass className="w-4 h-4 shrink-0" />
              <span>Open-Book Binder & Exam Day Guide</span>
            </button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:pt-16 sm:pb-28 border-b border-slate-200/80 dark:border-slate-800/80 bg-gradient-to-b from-white via-indigo-50/20 to-slate-50 dark:from-slate-950 dark:via-indigo-950/20 dark:to-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Hero Content */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 px-3.5 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800/80 text-indigo-700 dark:text-indigo-300 text-xs font-semibold shadow-xs">
                <Sparkles className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>The Gold Standard Life Sciences Exam Platform</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.15]">
                Pass Your GxP & RAC Exams on the <span className="bg-gradient-to-r from-indigo-600 via-cyan-600 to-indigo-600 bg-clip-text text-transparent">First Attempt</span>.
              </h1>

              <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                The only adaptive exam simulator built directly on Title 21 CFR Parts 11, 210, 211, 820, EU GMP Annex 1 (2022), and ICH Guidelines. Master realistic scenario dilemmas with SM-2 spaced repetition and authentic Prometric exam conditions.
              </p>

              {/* Value Pill Badges */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 pt-2 text-xs font-mono text-slate-600 dark:text-slate-400">
                <span className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1.5 rounded-xl shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Scenario-Based Practice
                </span>
                <span className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1.5 rounded-xl shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Prometric CBT Simulation
                </span>
                <span className="flex items-center gap-1.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-1.5 rounded-xl shadow-xs">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" /> 550 ASQ Pass Benchmark
                </span>
              </div>

              {/* Instant Social Proof */}
              <div className="pt-4 flex items-center justify-center lg:justify-start space-x-4 text-xs text-slate-500 dark:text-slate-400">
                <div className="flex -space-x-2 overflow-hidden">
                  <img className="inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-900" src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&auto=format&fit=crop&q=80" alt="Specialist" />
                  <img className="inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-900" src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&auto=format&fit=crop&q=80" alt="Specialist" />
                  <img className="inline-block h-8 w-8 rounded-full ring-2 ring-white dark:ring-slate-900" src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&auto=format&fit=crop&q=80" alt="Specialist" />
                </div>
                <span>Trusted by QA/QC Engineers, Regulatory Associates, and Plant Directors</span>
              </div>
            </div>

            {/* Right Hero Conversion Card: Sign in with Google & 14-Day Free Trial */}
            <div className="lg:col-span-5">
              <div className="bg-white dark:bg-slate-900 border-2 border-indigo-500/30 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-indigo-500/10 dark:shadow-none space-y-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 bg-gradient-to-l from-indigo-500/20 to-transparent w-32 h-32 rounded-full blur-2xl pointer-events-none" />

                <div className="space-y-2 text-center">
                  <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 text-xs font-bold font-mono">
                    <Check className="w-3.5 h-3.5" /> 14-DAY FULL ACCESS TRIAL
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                    Start Your Exam Prep
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Sign in with Google to unlock all scenario drills and spaced repetition tracking.
                  </p>
                </div>

                {/* Error Banner */}
                {(error || authError) && (
                  <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-start gap-2 text-xs text-rose-700 dark:text-rose-300">
                    <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
                    <span>{error || authError}</span>
                  </div>
                )}

                {/* Google Sign In Integration */}
                <div className="space-y-3.5">
                  <div ref={heroGoogleBtnRef} className={`flex justify-center w-full min-h-[44px] ${!isGsiRendered ? 'hidden' : ''}`} />

                  {!isGsiRendered && (
                    <button
                      onClick={handleGoogleSignIn}
                      disabled={isAuthenticating}
                      type="button"
                      className="w-full flex items-center justify-center space-x-3 px-5 py-3 rounded-2xl bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border-2 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 font-semibold text-sm shadow-sm transition active:scale-[0.99] cursor-pointer"
                    >
                      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z" />
                        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
                        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                      </svg>
                      <span>{isAuthenticating ? 'Connecting...' : 'Continue with Google'}</span>
                    </button>
                  )}

                  {/* 1-Click Demo Evaluation */}
                  <button
                    type="button"
                    onClick={handleDemoSignIn}
                    disabled={isAuthenticating}
                    className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 transition cursor-pointer"
                  >
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                      <span>Instant Demo: Dr. Evelyn Reed, RAC</span>
                    </span>
                    <span className="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/80 px-2 py-0.5 rounded-md border border-indigo-200/50">
                      Preview Now
                    </span>
                  </button>
                </div>

                {/* Features Checklist */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80 space-y-2 text-xs text-slate-500 dark:text-slate-400 font-mono">
                  <div className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Instant access to ASQ CPGP & RAPS RAC</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <span>Cancel anytime with 1-click customer portal</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 text-emerald-500" />
                    <span>21 CFR Part 11 Electronic Records Security</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Metrics Bar */}
      <section className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 py-8 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold font-mono text-indigo-600 dark:text-indigo-400">Prometric</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">CBT Exam Engine</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold font-mono text-slate-900 dark:text-white">100% BoK</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">ASQ & RAPS Coverage</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold font-mono text-emerald-600 dark:text-emerald-400">550 / 750</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Official ASQ Pass Benchmark</div>
            </div>
            <div className="space-y-1">
              <div className="text-3xl sm:text-4xl font-extrabold font-mono text-slate-900 dark:text-white">&lt; 50ms</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold">Edge API Latency</div>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Tracks Section */}
      <section id="tracks" className="py-16 sm:py-24 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center justify-center gap-1.5">
              <Layers className="w-4 h-4" /> Two Specialized Career Pathways
            </h2>
            <p className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Aligned with the Official Bodies of Knowledge
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Every scenario presents a practical dilemma with realistic production line parameters, analytical data, or regulatory submissions.
            </p>
          </div>

          {/* Track Switcher Tabs */}
          <div className="flex justify-center">
            <div className="bg-white dark:bg-slate-900 p-1.5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm inline-flex">
              <button
                onClick={() => setSelectedTrackTab('cpgp')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
                  selectedTrackTab === 'cpgp'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Award className="w-4 h-4" />
                <span>ASQ CPGP Track</span>
              </button>
              <button
                onClick={() => setSelectedTrackTab('rac')}
                className={`px-5 py-2.5 rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer ${
                  selectedTrackTab === 'rac'
                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/20'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                <Award className="w-4 h-4" />
                <span>RAPS RAC Drugs Track</span>
              </button>
            </div>
          </div>

          {/* Track Display Card */}
          {selectedTrackTab === 'cpgp' ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-sm space-y-8 animate-fadeIn">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 dark:border-slate-800 pb-6">
                <div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-xs font-bold mb-2">
                    <span>American Society for Quality • Certified Pharmaceutical GMP Professional</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    ASQ CPGP Examination Track
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
                    165-question Prometric CBT simulation (258 minutes) grounded in Title 21 CFR 210/211/820, EU GMP Volume 4 Annex 1 (2022), and ICH Q7-Q10.
                  </p>
                </div>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition shadow-sm self-start md:self-auto cursor-pointer"
                >
                  Start CPGP Simulation
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                {[
                  { dom: 'Domain I', name: 'Regulatory Environment & Governance', weight: '20% BoK' },
                  { dom: 'Domain II', name: 'Quality Management Systems & CAPA', weight: '20% BoK' },
                  { dom: 'Domain III', name: 'Laboratory Systems & OOS Investigations', weight: '20% BoK' },
                  { dom: 'Domain IV', name: 'Sterile Manufacturing & Annex 1 (2022)', weight: '20% BoK' },
                  { dom: 'Domain V', name: 'Validation Lifecycle & Part 11 Data Integrity', weight: '20% BoK' },
                ].map((d, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200/80 dark:border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="font-bold text-indigo-600 dark:text-indigo-400">{d.dom}</span>
                      <span className="text-emerald-600 dark:text-emerald-400">{d.weight}</span>
                    </div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 leading-snug">{d.name}</div>
                    <div className="text-[11px] font-mono text-slate-500">Official Syllabus Scope</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-sm space-y-8 animate-fadeIn">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-slate-100 dark:border-slate-800 pb-6">
                <div>
                  <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 text-xs font-bold mb-2">
                    <span>Regulatory Affairs Professionals Society • RAC (Drugs)</span>
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                    RAPS RAC Drugs Examination Track
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-2xl">
                    100-question regulatory simulation (150 minutes) covering global drug lifecycle: IND, 505(b)(2) vs 505(j), CTD Module 3 CMC, and pharmacovigilance.
                  </p>
                </div>
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs transition shadow-sm self-start md:self-auto cursor-pointer"
                >
                  Start RAC Simulation
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                  { dom: 'Domain I', name: 'Strategic Development & Expedited Pathways', weight: '25% BoK' },
                  { dom: 'Domain II', name: 'Clinical Development & IND Operations', weight: '25% BoK' },
                  { dom: 'Domain III', name: 'Marketing Applications (NDA/BLA/CTD)', weight: '25% BoK' },
                  { dom: 'Domain IV', name: 'Post-Approval Lifecycle & Pharmacovigilance', weight: '25% BoK' },
                ].map((d, i) => (
                  <div key={i} className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200/80 dark:border-slate-800/80 space-y-2">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="font-bold text-indigo-600 dark:text-indigo-400">{d.dom}</span>
                      <span className="text-emerald-600 dark:text-emerald-400">{d.weight}</span>
                    </div>
                    <div className="text-xs font-semibold text-slate-800 dark:text-slate-200 leading-snug">{d.name}</div>
                    <div className="text-[11px] font-mono text-slate-500">Official Syllabus Scope</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Feature Deep Dive Section */}
      <section id="features" className="py-16 sm:py-24 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          <div className="text-center max-w-3xl mx-auto space-y-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center justify-center gap-1.5">
              <Zap className="w-4 h-4 text-amber-500" /> Prometric CBT Architecture
            </h2>
            <p className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Engineered to Match Prometric Test-Day Reality
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
              Don't be surprised on exam day. Practice inside the exact cognitive conditions you'll encounter at Prometric testing centers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="p-6 sm:p-8 rounded-3xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4 hover:border-indigo-400 transition shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800/80 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                <Clock className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                1.56-Minute Pacing & 165-Q Mocks
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                ASQ allocates 258 minutes for 165 questions. Our strict Prometric countdown timer alerts you with urgent 5-minute warnings to guarantee you finish all questions.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="p-6 sm:p-8 rounded-3xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4 hover:border-emerald-400 transition shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800/80 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
                <Brain className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                SuperMemo-2 (SM-2) Spaced Recall
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Missed an OOS investigation question? Our SM-2 engine automatically schedules a targeted re-drill along your memory decay curve to lock in statutory retention.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="p-6 sm:p-8 rounded-3xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-4 hover:border-purple-400 transition shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-purple-50 dark:bg-purple-950/60 border border-purple-200 dark:border-purple-800/80 flex items-center justify-center text-purple-600 dark:text-purple-400">
                <Scale className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Distractor Elimination & Review Grid
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                Cross out incorrect distractors with 1 click (`line-through`) and navigate the full question review grid with flagging (hotkey `F`) and grid jump (hotkey `G`).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison: BioPharm vs. Legacy ASQ Question Bank */}
      <section id="comparison" className="py-16 sm:py-24 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950 transition-colors duration-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h2 className="text-xs font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400 flex items-center justify-center gap-1.5">
              <BarChart3 className="w-4 h-4" /> The Modern Alternative
            </h2>
            <p className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Why Candidates Choose BioPharm Over Official Banks
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl shadow-sm overflow-hidden text-xs">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-950/80">
                  <th className="p-4 sm:p-5 font-bold text-slate-700 dark:text-slate-300">Feature Comparison</th>
                  <th className="p-4 sm:p-5 font-bold text-indigo-600 dark:text-indigo-400 text-sm">BioPharm Exam Prep</th>
                  <th className="p-4 sm:p-5 font-bold text-slate-400 dark:text-slate-500">Official Society Banks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 text-slate-600 dark:text-slate-400">
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Question Depth</td>
                  <td className="p-4 sm:p-5 font-bold text-indigo-600 dark:text-indigo-400 font-mono">Comprehensive Scenario Databank</td>
                  <td className="p-4 sm:p-5">150–200 static questions</td>
                </tr>
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Question Style</td>
                  <td className="p-4 sm:p-5 font-medium text-emerald-600 dark:text-emerald-400">60+ word realistic production & lab dilemmas</td>
                  <td className="p-4 sm:p-5">Short definition & recall items</td>
                </tr>
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Adaptive Spaced Repetition</td>
                  <td className="p-4 sm:p-5 font-bold text-indigo-600 dark:text-indigo-400">SM-2 optimal memory decay algorithm</td>
                  <td className="p-4 sm:p-5">None (static review)</td>
                </tr>
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Prometric CBT Simulator</td>
                  <td className="p-4 sm:p-5 font-medium text-emerald-600 dark:text-emerald-400">Exact CBT pacing, strikeout distractor, review grid</td>
                  <td className="p-4 sm:p-5">Basic web flashcard interface</td>
                </tr>
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Scoring Benchmark</td>
                  <td className="p-4 sm:p-5 font-bold text-indigo-600 dark:text-indigo-400">Estimated ASQ Scaled Score (100–750, 550 Pass)</td>
                  <td className="p-4 sm:p-5">Raw percentage only</td>
                </tr>
                <tr>
                  <td className="p-4 sm:p-5 font-semibold text-slate-900 dark:text-slate-200">Free Trial Offer</td>
                  <td className="p-4 sm:p-5 font-bold text-emerald-600 dark:text-emerald-400">14-Day Unlimited Free Trial</td>
                  <td className="p-4 sm:p-5">$199–$249 upfront (no trial)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section with 14-Day Free Trial */}
      <section id="pricing" className="py-16 sm:py-24 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 transition-colors duration-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 text-xs font-bold font-mono">
              <span>ZERO RISK • 14 DAYS FULL ACCESS</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Invest in Your Career Certification
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Exam fees cost $550+ per sitting. Our simulator ensures you pass on your first attempt.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <div className="bg-white dark:bg-slate-900 border-2 border-indigo-600 dark:border-indigo-500 rounded-3xl p-8 shadow-xl shadow-indigo-500/10 dark:shadow-none space-y-6 relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-indigo-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                Most Popular
              </div>

              <div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">Professional All-Access</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Full access to both ASQ CPGP & RAPS RAC Drugs tracks</p>
              </div>

              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-extrabold font-mono text-slate-900 dark:text-white">$49</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">/ month after 14-day free trial</span>
              </div>

              <div className="p-3.5 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-900/60 text-xs text-indigo-900 dark:text-indigo-300 font-medium">
                ⚡ <strong>Free for 14 Days:</strong> Unlimited access to all scenario practice modules, Prometric mocks, and spaced retention. Cancel anytime with 1 click.
              </div>

              <div className="space-y-3 pt-2 text-xs text-slate-600 dark:text-slate-300">
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Full ASQ CPGP scenario coverage across Domains I–V</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Full RAPS RAC Drugs scenario coverage across Domains I–IV</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Prometric CBT exam simulator with authentic timers</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>SuperMemo-2 adaptive spaced repetition engine</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Prometric Open-Book Binder Checklist & Study Guide</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span>Daily Micro-Challenge emails with streak tracking</span>
                </div>
              </div>

              <div className="pt-4">
                <button
                  onClick={() => setIsLoginModalOpen(true)}
                  className="w-full py-3 px-4 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm transition shadow-lg shadow-indigo-600/30 flex items-center justify-center space-x-2 cursor-pointer"
                >
                  <span>Start 14-Day Free Trial</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8 text-xs text-slate-500 dark:text-slate-400">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold text-xs">
              <Award className="w-4 h-4" />
            </div>
            <div>
              <div className="font-bold text-slate-900 dark:text-slate-200">BioPharm Exam Prep</div>
              <div className="text-[11px] font-mono">21 CFR Part 11 Electronic Records Security Compliant</div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 font-mono text-[11px]">
            <button onClick={() => setIsOpenBookModalOpen(true)} className="hover:underline cursor-pointer">Open-Book Binder Guide</button>
            <button onClick={() => setIsLoginModalOpen(true)} className="hover:underline cursor-pointer">Sign In with Google</button>
            <span>FDA 21 CFR 210/211/820</span>
            <span>EU GMP Annex 1 (2022)</span>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-6 text-[11px] text-slate-400 dark:text-slate-500 text-center leading-relaxed">
          BioPharm Exam Prep is an independent educational simulator. ASQ, CPGP, RAPS, and RAC are registered trademarks of their respective organizations. No endorsement is implied.
        </div>
      </footer>

      {/* Sign-In Modal (Triggered by any "Sign In" or "Start Trial" button) */}
      {isLoginModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl space-y-6 relative">
            <button
              onClick={() => setIsLoginModalOpen(false)}
              className="absolute top-5 right-5 p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center space-y-2">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center text-indigo-600 dark:text-indigo-400 mx-auto">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Sign In to BioPharm</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Continue with Google for secure 21 CFR Part 11 compliant audit logging and progress synchronization.
              </p>
            </div>

            {/* Error Banner */}
            {(error || authError) && (
              <div className="p-3 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 flex items-start gap-2 text-xs text-rose-700 dark:text-rose-300">
                <AlertCircle className="w-4 h-4 shrink-0 text-rose-600 mt-0.5" />
                <span>{error || authError}</span>
              </div>
            )}

            <div className="space-y-3.5">
              <div ref={modalGoogleBtnRef} className={`flex justify-center w-full min-h-[44px] ${!isGsiRendered ? 'hidden' : ''}`} />

              {!isGsiRendered && (
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isAuthenticating}
                  type="button"
                  className="w-full flex items-center justify-center space-x-3 px-5 py-3 rounded-2xl bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border-2 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 font-semibold text-sm shadow-sm transition cursor-pointer"
                >
                  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.66-5.17 3.66-9.17z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                  </svg>
                  <span>{isAuthenticating ? 'Connecting...' : 'Sign in with Google'}</span>
                </button>
              )}

              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleDemoSignIn}
                  disabled={isAuthenticating}
                  className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 transition cursor-pointer"
                >
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
                    <span>Demo Profile: Dr. Evelyn Reed, RAC</span>
                  </span>
                  <span className="text-[10px] font-mono text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/80 px-2 py-0.5 rounded-md">
                    Instant Demo
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prometric Exam Day & Open-Book Binder Guide Modal */}
      {isOpenBookModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2.5 rounded-2xl bg-indigo-500/15 text-indigo-600 dark:text-indigo-400 border border-indigo-500/30">
                  <Compass className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                    Official ASQ Exam Day & Open-Book Binder Guide
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Prometric test-center regulations, permitted reference materials, and pacing strategies
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsOpenBookModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                <BookOpen className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span>What Can You Bring Into Prometric?</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                The ASQ CPGP is an <strong>open-book examination</strong>. However, Prometric test proctors enforce strict binding standards.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div className="p-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-800 dark:text-emerald-300">
                    <CheckCircle2 className="w-4 h-4" /> Permitted in the Testing Room:
                  </div>
                  <ul className="text-xs text-emerald-900/90 dark:text-emerald-200/90 space-y-1.5 list-disc list-inside">
                    <li><strong>The Certified Pharmaceutical GMP Professional Handbook</strong> (Durivage / ASQ Quality Press)</li>
                    <li>Clean, printed copies of <strong>21 CFR Parts 11, 210, 211, 820</strong> and <strong>EU GMP EudraLex Vol 4 & Annex 1</strong></li>
                    <li>Reference books with glued bindings or spiral/comb/ring binders</li>
                    <li>Permanent index tabs attached to pages (e.g. labeled tabs for CFR parts)</li>
                  </ul>
                </div>

                <div className="p-4 rounded-2xl bg-rose-50/70 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-rose-800 dark:text-rose-300">
                    <AlertCircle className="w-4 h-4" /> Strictly Prohibited (Disqualification Risk):
                  </div>
                  <ul className="text-xs text-rose-900/90 dark:text-rose-200/90 space-y-1.5 list-disc list-inside">
                    <li><strong>Loose papers or unbound sheets</strong> of any kind</li>
                    <li>Sticky notes or post-it flags with <strong>handwritten text or notes</strong></li>
                    <li>Smartphones, smartwatches, or tablets</li>
                    <li>Calculators with full QWERTY keyboards or programmable text memory</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="pt-4 flex justify-end border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setIsOpenBookModalOpen(false)}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold transition cursor-pointer"
              >
                Close Guide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
